/**
 * Pressure Role - Validate Pillar
 *
 * Validação de valores de pressão.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validatePressure, isValidPressure } from '@attrx/role-morphic/pressure/validate';
 *
 * validatePressure(100);   // { valid: true, errors: [] }
 * validatePressure(-5);    // { valid: false, errors: ['Pressure cannot be negative'] }
 * isValidPressure(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface PressureValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
  /** Permitir valores negativos (default: false) */
  allowNegative?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const PRESSURE_VALIDATION_CONFIG: PressureValidationConfig = {
  min: 0,
  minError: "Pressure cannot be negative",
  allowNegative: false,
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de pressão
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validatePressure(100);                    // { valid: true, errors: [] }
 * validatePressure(-5);                     // { valid: false, errors: ['Pressure cannot be negative'] }
 * validatePressure(-5, { allowNegative: true }); // { valid: true, errors: [] }
 * validatePressure(1000, { max: 500 });     // { valid: false, errors: ['Value must be at most 500'] }
 */
export function validatePressure(
  value: unknown,
  config: PressureValidationConfig = PRESSURE_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Merge with defaults
  const effectiveConfig = { ...PRESSURE_VALIDATION_CONFIG, ...config };

  // Check negative
  if (!effectiveConfig.allowNegative && value < 0) {
    errors.push(effectiveConfig.minError || "Pressure cannot be negative");
  }

  // Check min (if allowNegative is true and min is set)
  if (
    effectiveConfig.allowNegative &&
    effectiveConfig.min !== undefined &&
    value < effectiveConfig.min
  ) {
    errors.push(effectiveConfig.minError || `Value must be at least ${effectiveConfig.min}`);
  }

  // Check max
  if (effectiveConfig.max !== undefined && value > effectiveConfig.max) {
    errors.push(effectiveConfig.maxError || `Value must be at most ${effectiveConfig.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de pressão é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidPressure(100);  // true
 * isValidPressure(-5);   // false
 */
export function isValidPressure(
  value: unknown,
  config?: PressureValidationConfig,
): boolean {
  return validatePressure(value, config).valid;
}
